import type { RubricGates } from './creative-process.types'

/**
 * The expert layer behind the composer scorecard. Every dimension, gate, and
 * verdict the engine reports gets an explainer here: what it means, why it moves
 * the post, what weak vs strong looks like, and a one-tap fix that pre-fills the
 * composer chat. This is where Taka shows it knows the material.
 *
 * Copy is authored from the engine's own rubric, not invented:
 *   - gates + 11 static dimensions + scale: apps/socialkit/src/core/visual/v2/judge.ts
 *   - carousel dimensions: apps/socialkit/src/core/visual/v2/carousel-judge.ts
 *   - reel dimensions: apps/socialkit/src/core/visual/v2/video-judge.ts
 */

export interface ExplainerAction {
  /** Button label, viewer's voice (e.g. "Sharpen the hook"). */
  label: string
  /** Instruction dropped into the composer chat input, not auto-sent. */
  prompt: string
}

export interface Explainer {
  id: string
  label: string
  /** The definition. Reads cleanly for a newcomer. */
  oneLiner: string
  /** The expert "why it matters", grounded in the rubric / platform algorithm. */
  why: string
  /** What the low and high end of the scale actually look like. */
  scale?: { low: string; high: string }
  /** Surfaced as a badge, e.g. "Weighted 3x". Only where the rubric weights it. */
  weightNote?: string
  /** Short mono chips, e.g. ["swap test"]. */
  tags?: string[]
  action?: ExplainerAction
}

export type ScoreRubric = 'static' | 'carousel' | 'video'

const SHIP_SCALE = {
  low: '1 = slop. Vague, generic, or off-brand.',
  high: '5 = postable. A business would proudly publish it.',
}
const DECK_SCALE = {
  low: '1 = template tell. Interchangeable, flat, padded.',
  high: '10 = studio-grade. Work a real agency would ship.',
}
const GATE_SCALE = {
  low: 'FAIL caps the verdict at no-ship, however good the rest is.',
  high: 'PASS clears the bar and lets the score stand on its own.',
}

// ---------------------------------------------------------------------------
// Concepts (the score itself)
// ---------------------------------------------------------------------------

const CONCEPTS: Record<'quality' | 'weights' | 'swapTest', Explainer> = {
  quality: {
    id: 'concept.quality',
    label: 'The Quality score',
    oneLiner:
      'A 0 to 100 read on how ready this post is to ship, from the same judge a brand social manager applies before they hit publish.',
    why: 'It is not a vanity number. The engine scores the finished post against a strict rubric: four hard gates, then weighted dimensions of craft. Higher means more likely to stop the scroll, earn the save, and sound like you. 85+ is strong, 70+ is solid, below that needs work.',
    scale: {
      low: 'Below 70: something real is off. Open the breakdown to see what.',
      high: '85+: strong. Ship it or push for great.',
    },
    tags: ['0-100'],
  },
  weights: {
    id: 'concept.weights',
    label: 'Why dimensions are weighted',
    oneLiner:
      'Not every dimension counts equally. Specificity, voice, and visual craft carry the most weight because they decide whether a post lands.',
    why: 'A grammatically perfect post that says nothing still fails. The rubric weights the levers that actually move reach and trust: specificity (×3), voice (×3), and visual craft (×3) over hygiene checks (×1). Fix the heavy ones first.',
    tags: ['rubric'],
  },
  swapTest: {
    id: 'concept.swapTest',
    label: 'The swap test',
    oneLiner:
      'Could a competitor in another city post this exact thing unchanged? If yes, it fails the swap test.',
    why: 'It is the single sharpest filter for generic content. The engine runs it on your copy and imagery. A post that survives the swap test contains real, checkable detail that is true only of your business: a named product, a number, a place, a moment. Everything generic gets caught here.',
    tags: ['specificity'],
    action: {
      label: 'Make it pass the swap test',
      prompt:
        'Rewrite this so it could only come from my business. Add a specific, checkable detail (a named product, a number, a place, or a real moment) that a competitor could not copy.',
    },
  },
}

// ---------------------------------------------------------------------------
// The four hard gates (PASS / FAIL)
// ---------------------------------------------------------------------------

const GATES: Record<keyof RubricGates, Explainer> = {
  specificity: {
    id: 'gate.specificity',
    label: 'Specific',
    oneLiner: 'Passes only if the post contains real, checkable detail unique to your business.',
    why: 'This is the swap test as a hard gate. If a competitor in another city could post it unchanged, it fails, and a fail caps the verdict at no-ship no matter how polished the rest is. Specificity is the difference between marketing and noise.',
    scale: GATE_SCALE,
    tags: ['swap test', 'hard gate'],
    action: {
      label: 'Add specific detail',
      prompt:
        'This is too generic. Add a concrete, checkable detail unique to my business (a named product, a real number, a place, or a specific moment) so it passes the swap test.',
    },
  },
  truth: {
    id: 'gate.truth',
    label: 'True to facts',
    oneLiner:
      'Passes only if every claim is true and provable, with no unbacked superlatives or invented people and results.',
    why: 'A single false or unverifiable claim fails the gate. "Best in the city" with no proof, a fabricated testimonial, a made-up stat: all caps the post at no-ship. Trust is the asset, and one inflated claim spends it.',
    scale: GATE_SCALE,
    tags: ['hard gate'],
    action: {
      label: 'Ground the claims',
      prompt:
        'Make every claim here provable. Replace any superlative or unbacked statement with something true and specific I can stand behind.',
    },
  },
  visualIntegrity: {
    id: 'gate.visualIntegrity',
    label: 'Visual integrity',
    oneLiner: 'Passes only if the image is artifact-free and the on-image text is fully legible.',
    why: 'This gate is about the picture, not the wording. It fails for AI tells in the generated photo (plastic-looking people, extra fingers, warped objects, render-glow, gibberish baked into the image) or for overlaid text that is clipped, overlapping, or unreadable. One visible artifact reads as amateur and sinks the whole post.',
    scale: GATE_SCALE,
    tags: ['imagery', 'hard gate'],
    action: {
      label: 'Remake the image',
      prompt:
        'The image has a visual problem. Regenerate it cleanly with no artifacts and make sure all on-image text is fully legible.',
    },
  },
  ownerPride: {
    id: 'gate.ownerPride',
    label: 'Owner pride',
    oneLiner:
      'Passes only if you would proudly put your name on it, with no "that is not us" or "I would be embarrassed".',
    why: 'The final human check. A post can be specific, true, and clean and still feel off-brand or cringe. This gate fails when the owner would wince. It is the line between technically correct and actually yours.',
    scale: GATE_SCALE,
    tags: ['hard gate'],
    action: {
      label: 'Make it feel like us',
      prompt:
        'This does not feel like my brand. Rework the angle and tone so it is something I would be proud to post under my own name.',
    },
  },
}

// ---------------------------------------------------------------------------
// Verdicts
// ---------------------------------------------------------------------------

const VERDICTS: Record<string, Explainer> = {
  SHIP: {
    id: 'verdict.SHIP',
    label: 'Ship',
    oneLiner: 'Clears every gate and scores strong. Post it as is.',
    why: 'No hard gate failed and the weighted craft is high. The engine would publish this unchanged. You can still push for great, but nothing is holding it back.',
    tags: ['postable'],
  },
  SHIP_WITH_ONE_FIX: {
    id: 'verdict.SHIP_WITH_ONE_FIX',
    label: 'Ship with one fix',
    oneLiner: 'Strong overall, with a single highest-leverage change that would lift it.',
    why: 'Gates pass and the post is postable, but one move would meaningfully raise the score. Make it, or ship as is.',
    tags: ['postable'],
    action: {
      label: 'Apply the one fix',
      prompt: 'Apply the single highest-leverage fix the scorecard suggested to lift this post.',
    },
  },
  REVISE: {
    id: 'verdict.REVISE',
    label: 'Revise',
    oneLiner: 'Not there yet. Real craft gaps to close before this is worth posting.',
    why: 'No gate hard-failed, but the weighted dimensions are too weak to ship. Open the breakdown, find the lowest heavy dimensions (specificity, voice, visual craft), and fix those first.',
    action: {
      label: 'Revise it',
      prompt: 'Revise this post to fix its weakest dimensions and get it to a postable score.',
    },
  },
  REJECT: {
    id: 'verdict.REJECT',
    label: 'Reject',
    oneLiner: 'Far from postable. Better to regenerate than to patch.',
    why: 'The post is weak across the board. Rather than fixing line by line, give the engine a sharper brief and start fresh.',
    action: {
      label: 'Start fresh',
      prompt: 'This is not working. Generate a new version with a sharper, more specific angle.',
    },
  },
  NO_SHIP_GATE_FAIL: {
    id: 'verdict.NO_SHIP_GATE_FAIL',
    label: 'Gate failed',
    oneLiner: 'A hard gate failed, which caps the verdict at no-ship regardless of the score.',
    why: 'One of the four gates (specific, true, visual integrity, owner pride) failed. A gate fail overrides everything, so even a high craft score cannot ship. Find the red gate chip and fix that first.',
    tags: ['hard gate'],
  },
  GREAT: {
    id: 'verdict.GREAT',
    label: 'Great',
    oneLiner: 'Top tier for a carousel. The kind of deck a real studio would ship.',
    why: 'Reserved for decks that earn the swipe, build a real arc, stay cohesive, and finish clean on every slide. This is the bar, not the average.',
    tags: ['postable'],
  },
  STRONG: {
    id: 'verdict.STRONG',
    label: 'Strong',
    oneLiner: 'A solid, postable carousel with a clear arc and cohesive look.',
    why: 'Above the template line: a real hook, one idea per slide, forward momentum, and a genuine CTA. Push the weakest deck dimension to reach great.',
    tags: ['postable'],
  },
  COMPETENT: {
    id: 'verdict.COMPETENT',
    label: 'Competent',
    oneLiner: 'Clean but unremarkable. On-brand without being memorable.',
    why: 'Nothing is broken, but nothing pulls. Usually a flat hook, a slide 2 that restates slide 1, or layouts that repeat. Sharpen the hook and vary the slide roles to climb.',
    action: {
      label: 'Make it stronger',
      prompt:
        'This carousel is competent but flat. Sharpen the slide 1 hook, make slide 2 advance the idea, and vary the slide layouts so it does not read as a template.',
    },
  },
  TEMPLATE: {
    id: 'verdict.TEMPLATE',
    label: 'Template',
    oneLiner:
      'Reads as a template: same layout every slide, a flat hook, a pile of interchangeable tips.',
    why: 'The slop tell for carousels. It is clean but generic, which means it gets swiped past. Rebuild the hook, give the slides distinct roles, and add forward pull.',
    action: {
      label: 'Break the template',
      prompt:
        'This carousel reads as a template. Rebuild it with a specific slide 1 hook, a real arc across slides, distinct layouts per slide, and forward momentum into a genuine CTA.',
    },
  },
}

/**
 * Reels reuse the same GREAT/STRONG/COMPETENT/TEMPLATE verdict tokens as carousels
 * (both come from `designTierForScore`), but a deck and a clip are different mediums.
 * These reel-flavored overrides keep the verdict lesson accurate for video — the
 * shared tokens above stay carousel-specific. Resolved when the rubric is `video`.
 */
const VIDEO_VERDICTS: Record<string, Explainer> = {
  GREAT: {
    id: 'verdict.GREAT',
    label: 'Great',
    oneLiner: 'Top tier for a reel. The kind of clip a real studio would ship.',
    why: 'Reserved for reels that hook in the first second, hold one clear arc, look authentic rather than over-polished, and finish clean. This is the bar, not the average.',
    tags: ['postable'],
  },
  STRONG: {
    id: 'verdict.STRONG',
    label: 'Strong',
    oneLiner: 'A solid, postable reel with a real hook and a clear arc.',
    why: 'Above the template line: a felt opening moment, authentic motion, beats that build, and a genuine CTA. Push the weakest beat to reach great.',
    tags: ['postable'],
  },
  COMPETENT: {
    id: 'verdict.COMPETENT',
    label: 'Competent',
    oneLiner: 'Clean but unremarkable. Watchable without being memorable.',
    why: 'Nothing is broken, but nothing pulls. Usually a slow open, repeated beats, or an over-polished look that reads as AI. Sharpen the first second and vary the beats to climb.',
    action: {
      label: 'Make it stronger',
      prompt:
        'This reel is competent but flat. Make the first second stop the scroll, vary the beats so they build, and cut any over-stylized shots so it feels real.',
    },
  },
  TEMPLATE: {
    id: 'verdict.TEMPLATE',
    label: 'Template',
    oneLiner: 'Reads as a template: slow open, generic look, beats that repeat.',
    why: 'The slop tell for reels. It is clean but generic, which means it gets scrolled. Rebuild the opening moment, vary the beats into a real arc, and give it an on-brand look.',
    action: {
      label: 'Break the template',
      prompt:
        'This reel reads as a template. Open on a real felt moment with a legible hook, vary the beats into a clear arc, and give it a cohesive on-brand look instead of a generic one.',
    },
  },
}

// ---------------------------------------------------------------------------
// Design tier (art direction look, separate from the overall score)
// ---------------------------------------------------------------------------

const DESIGN: Record<string, Explainer> = {
  GREAT: {
    id: 'design.GREAT',
    label: 'Great look',
    oneLiner: 'Studio-grade art direction: graded, integrated, on-brand, no template tells.',
    why: 'This is the look, judged separately from the words. "Great" is reserved for visuals a real design studio would ship as is. A post can score high on copy and still fall short here, and vice versa.',
    tags: ['art direction'],
  },
  STRONG: {
    id: 'design.STRONG',
    label: 'Strong look',
    oneLiner: 'Clean, on-brand art direction that holds together.',
    why: 'The visual is well above template territory: consistent styling, integrated imagery, a deliberate look. Judged separately from the copy score. One push on the art direction reaches great.',
    tags: ['art direction'],
  },
  COMPETENT: {
    id: 'design.COMPETENT',
    label: 'Solid look',
    oneLiner: 'Clean but unremarkable art direction. Nothing wrong, nothing memorable.',
    why: 'The look is fine but generic. This is about the design, not the words, so the copy can be strong while the visual still reads as ordinary. Distinctive styling and better integration lift it.',
    tags: ['art direction'],
  },
  TEMPLATE: {
    id: 'design.TEMPLATE',
    label: 'Looks templated',
    oneLiner: 'The art direction reads as a template: flat layout, generic styling.',
    why: 'This judges the LOOK, not the copy, which is why you can see a high overall score next to "looks templated". The words land; the design is generic. Graded imagery, on-brand type, and real integration fix it.',
    tags: ['art direction'],
    action: {
      label: 'Improve the design',
      prompt:
        'The look is too generic. Redesign it with on-brand styling, graded and integrated imagery, and a distinctive layout instead of a flat template.',
    },
  },
}

// ---------------------------------------------------------------------------
// Static post: the eleven weighted dimensions (1-5)
// ---------------------------------------------------------------------------

const STATIC: Record<string, Explainer> = {
  specificity: {
    id: 'static.specificity',
    label: 'Specificity',
    oneLiner: 'Named entities, numbers, and real scenes instead of abstractions.',
    why: 'The heaviest lever in the rubric. Specific copy survives the swap test and reads as true. "We use small-batch beans roasted Tuesdays" beats "premium quality coffee" every time. Detail is what makes a post yours.',
    scale: SHIP_SCALE,
    weightNote: 'Weighted 3x',
    tags: ['swap test'],
    action: {
      label: 'Make it more specific',
      prompt:
        'Make this more specific. Swap the vague phrases for named products, real numbers, and concrete scenes from my business.',
    },
  },
  voice: {
    id: 'static.voice',
    label: 'Voice',
    oneLiner: 'Sounds like a real person and a real brand, with no AI tells or banned phrases.',
    why: 'Weighted as heavily as specificity. Generic "elevate your business" copy and AI throat-clearing get punished hard. The score rewards copy that sounds like you actually talk, in your own register.',
    scale: SHIP_SCALE,
    weightNote: 'Weighted 3x',
    action: {
      label: 'Fix the voice',
      prompt:
        'Rewrite this in my real brand voice. Cut any generic AI phrasing and corporate filler so it sounds like a person, not a template.',
    },
  },
  visualCraft: {
    id: 'static.visualCraft',
    label: 'Visual craft',
    oneLiner: 'The look: legible on-image text, on-brand styling, and an artifact-free image.',
    why: 'The image is the first thing seen and is weighted accordingly. This rewards a clean, designed look where the text is readable at a glance and the picture is free of AI tells. Craft is what separates a post from a meme.',
    scale: SHIP_SCALE,
    weightNote: 'Weighted 3x',
    tags: ['imagery'],
    action: {
      label: 'Improve the visual',
      prompt:
        'Improve the visual craft: make the on-image text more legible and on-brand, and clean up the image so it looks designed, not generated.',
    },
  },
  hook: {
    id: 'static.hook',
    label: 'Hook',
    oneLiner: 'The first line and the on-image headline. It stops the thumb or it does not.',
    why: 'Most of a post is never read past the first line. A specific, surprising, or tension-loaded opener earns the rest. A bland title-card hook leaks all the reach the body could have earned.',
    scale: SHIP_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Sharpen the hook',
      prompt:
        'Rewrite the hook so the first line and the on-image headline stop the scroll. Lead with the most specific or surprising detail.',
    },
  },
  brandFit: {
    id: 'static.brandFit',
    label: 'Brand fit',
    oneLiner: 'How well the post matches your brand, audience, and the way you sound.',
    why: 'A strong post for the wrong brand still fails. This checks that the angle, tone, and look fit who you are and who you are talking to. It is the difference between on-trend and on-brand.',
    scale: SHIP_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Tighten brand fit',
      prompt: 'Adjust the angle and tone so this fits my brand and my audience more tightly.',
    },
  },
  focus: {
    id: 'static.focus',
    label: 'Focus',
    oneLiner: 'One clear idea, not three competing ones crammed into a single post.',
    why: 'Posts that try to say everything say nothing. The score rewards a single, sharp point. If the caption pulls in two directions, pick the stronger one and cut the rest.',
    scale: SHIP_SCALE,
    action: {
      label: 'Tighten the focus',
      prompt:
        'Narrow this to one clear idea. Cut the secondary points so the post makes a single sharp argument.',
    },
  },
  proof: {
    id: 'static.proof',
    label: 'Proof',
    oneLiner: 'Evidence that backs the claim: a result, a number, a name, a specific.',
    why: 'Claims without proof read as marketing. A real number, a named outcome, or a concrete example turns a statement into something believable. Proof is what makes specificity land.',
    scale: SHIP_SCALE,
    action: {
      label: 'Add proof',
      prompt:
        'Back the main claim with real proof: a result, a number, a named example, or a specific from my business.',
    },
  },
  cta: {
    id: 'static.cta',
    label: 'CTA',
    oneLiner: 'A clear next step with a real mechanism, not bait or an abrupt ending.',
    why: 'A good post earns the right to ask for something. The score rewards a genuine call to action (book, visit, reply, try) with a real way to do it, and punishes engagement bait like "comment below" with no substance.',
    scale: SHIP_SCALE,
    action: {
      label: 'Strengthen the CTA',
      prompt:
        'Give this a clear, genuine call to action with a real next step, not engagement bait.',
    },
  },
  languageHygiene: {
    id: 'static.languageHygiene',
    label: 'Language',
    oneLiner: 'Clean writing: no spam hashtags, no buzzword pileups, no broken grammar.',
    why: 'The hygiene check. Excess hashtags, exclamation spam, hedges, and hype words drag a post down even when the idea is good. Clean language signals a brand that pays attention.',
    scale: SHIP_SCALE,
    action: {
      label: 'Clean up the language',
      prompt:
        'Tidy the language: cut spam hashtags, buzzwords, and excess punctuation so it reads clean and confident.',
    },
  },
  format: {
    id: 'static.format',
    label: 'Format',
    oneLiner: 'How the post is laid out for the platform: line breaks, length, scanability.',
    why: 'The same words win or lose on formatting. Mobile readers scan, so line-break rhythm and the right length for the platform decide whether the post is read at all.',
    scale: SHIP_SCALE,
    action: {
      label: 'Fix the format',
      prompt:
        'Reformat this for the platform: better line breaks, the right length, and an easy mobile scan.',
    },
  },
  coherence: {
    id: 'static.coherence',
    label: 'Coherence',
    oneLiner: 'The caption, on-image text, and picture tell one connected story.',
    why: 'When the image says one thing and the caption another, the post feels assembled, not made. Coherence rewards a post where every part carries the same idea and pulls in the same direction.',
    scale: SHIP_SCALE,
    action: {
      label: 'Make it cohere',
      prompt:
        'Make the caption, on-image text, and image tell one connected story so the post feels like a single piece.',
    },
  },
}

// ---------------------------------------------------------------------------
// Carousel: ten deck-level dimensions (1-10)
// ---------------------------------------------------------------------------

const CAROUSEL: Record<string, Explainer> = {
  hookStrength: {
    id: 'carousel.hookStrength',
    label: 'Hook',
    oneLiner: 'Does slide 1 earn the swipe with a specific, numbered, or tension-loaded promise?',
    why: 'Slide 1 is the whole funnel. A bland title card gets swiped past and the rest of the deck never gets seen. A specific or surprising opener buys every slide after it.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Sharpen slide 1',
      prompt:
        'Rewrite slide 1 so it earns the swipe: lead with a specific number, a tension, or a surprising promise instead of a flat title.',
    },
  },
  narrativeArc: {
    id: 'carousel.narrativeArc',
    label: 'Arc',
    oneLiner:
      'Do the slides build in a deliberate order, or are they a pile of interchangeable tips?',
    why: 'A great deck is a sequence, not a list. When slides could be shuffled with no loss, there is no arc and no reason to keep swiping. Order should build toward a payoff.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Build a real arc',
      prompt:
        'Reorder and rewrite the slides so they build a deliberate arc toward a payoff, not an interchangeable list of tips.',
    },
  },
  slideTwo: {
    id: 'carousel.slideTwo',
    label: 'Slide 2',
    oneLiner:
      'Does slide 2 stand on its own as a second hook and advance, instead of restating slide 1?',
    why: 'The biggest drop-off is between slide 1 and 2. If slide 2 just repeats the hook, viewers leave. It needs to add a new beat that pulls them deeper.',
    scale: DECK_SCALE,
    action: {
      label: 'Fix slide 2',
      prompt:
        'Rewrite slide 2 so it acts as a second hook and advances the idea, instead of restating slide 1.',
    },
  },
  oneIdea: {
    id: 'carousel.oneIdea',
    label: 'One idea',
    oneLiner: 'One idea per slide, with no wall of text on any single slide.',
    why: 'Carousels are read at a glance per slide. A slide carrying two ideas or a paragraph of text breaks the rhythm and gets skipped. One clear thought per card.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'One idea per slide',
      prompt:
        'Tighten each slide to a single idea and cut any wall of text so every slide reads at a glance.',
    },
  },
  momentum: {
    id: 'carousel.momentum',
    label: 'Momentum',
    oneLiner:
      'Does each body slide end with forward pull (an open loop or a numbered next), with no stalls?',
    why: 'Momentum is what carries a reader to the last slide. A slide that closes the loop too early or stalls kills the swipe. Each one should make the next feel necessary.',
    scale: DECK_SCALE,
    action: {
      label: 'Add forward pull',
      prompt:
        'Give each body slide forward momentum: end on an open loop or a numbered next so the reader has to keep swiping.',
    },
  },
  cohesion: {
    id: 'carousel.cohesion',
    label: 'Cohesion',
    oneLiner:
      'One palette, type, and motif system across the whole set, so it reads as one designed piece.',
    why: 'A deck where slides look like they came from different templates reads as amateur. Cohesion (shared color, type, grade) makes the set feel like one intentional piece of work.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Unify the look',
      prompt:
        'Make the deck cohesive: one palette, type system, and motif across every slide so it reads as a single designed piece.',
    },
  },
  roleVariation: {
    id: 'carousel.roleVariation',
    label: 'Variation',
    oneLiner:
      'Do the hook, body, and CTA layouts differ enough to avoid monotony while staying unified?',
    why: 'Cohesion without variation is a template. The trick is slides that share a system but play distinct roles, so the deck feels designed rather than mass-produced. Same identity, different layouts.',
    scale: DECK_SCALE,
    action: {
      label: 'Vary the layouts',
      prompt:
        'Vary the slide layouts so the hook, body, and CTA slides look distinct while staying in one cohesive system.',
    },
  },
  cta: {
    id: 'carousel.cta',
    label: 'CTA',
    oneLiner: 'Does the final slide give a real next step, not bait or an abrupt stop?',
    why: 'The payoff slide is where a deck converts attention into action. A genuine CTA with a mechanism (follow, save, book, visit) beats "thanks for reading" or an engagement-bait prompt.',
    scale: DECK_SCALE,
    action: {
      label: 'Land the CTA',
      prompt:
        'Rewrite the final slide as a real call to action with a clear next step, not bait or an abrupt ending.',
    },
  },
  lengthDiscipline: {
    id: 'carousel.lengthDiscipline',
    label: 'Length',
    oneLiner: 'Does the slide count fit the substance, with no padding and nothing too thin?',
    why: 'Decks padded to hit a number bore, and decks too thin to deliver disappoint. The right length is exactly as many slides as the idea needs.',
    scale: DECK_SCALE,
    action: {
      label: 'Right-size the deck',
      prompt:
        'Adjust the slide count to fit the substance: cut padding slides and add depth where it is too thin.',
    },
  },
  finish: {
    id: 'carousel.finish',
    label: 'Finish',
    oneLiner:
      'Does each slide pass the static bar: graded, integrated, on-brand, no flat-box template tell?',
    why: 'Finish is the per-slide polish. A flat color box slapped over a photo or untreated stock imagery breaks the spell. Every slide should look as deliberate as a single post.',
    scale: DECK_SCALE,
    action: {
      label: 'Polish the slides',
      prompt:
        'Raise the finish on each slide: properly graded and integrated imagery, on-brand styling, no flat-box template look.',
    },
  },
}

// ---------------------------------------------------------------------------
// Reel: ten clip-level dimensions (1-10)
// ---------------------------------------------------------------------------

const VIDEO: Record<string, Explainer> = {
  hookStrength: {
    id: 'video.hookStrength',
    label: 'Hook',
    oneLiner:
      'Frame 1 plus the hook text: a felt moment and a legible promise that stops the scroll.',
    why: 'Video is won or lost in the first second. A slow open, an empty frame, or a logo intro leaks the entire view. The opening frame needs a real moment and a clear reason to stay.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Fix the opening',
      prompt:
        'Make the first second stop the scroll: open on a real, felt moment with a legible hook, not a slow build or a logo.',
    },
  },
  wouldPost: {
    id: 'video.wouldPost',
    label: 'Would post',
    oneLiner: 'The anti-ad test: does this read as content a real person posted, or as marketing?',
    why: 'Viewers skip ads. A clip with ad-copy enthusiasm, a value-prop lecture, or a corporate register gets scrolled no matter how polished it is. The reels that travel read like the owner posted them: dry, specific, self-aware.',
    scale: {
      low: '1 = reads as an ad. Salesy, corporate, tool-made.',
      high: '10 = reads as a post a real person made for fun.',
    },
    weightNote: 'Weighted 2x',
    action: {
      label: 'Make it feel human',
      prompt:
        'Rewrite this so it reads like the owner posted it for fun, not like an ad: drier, more specific, more self-aware, no sales language.',
    },
  },
  motionAuthenticity: {
    id: 'video.motionAuthenticity',
    label: 'Authenticity',
    oneLiner: 'Does the footage look like real filmed footage, not AI slop?',
    why: 'Authenticity beats polish on social video. Over-sharp cinematic sheen, plastic or morphing surfaces, and uncanny motion read as fake and get scrolled. A clip that looks "too good" scores lower than one that looks real.',
    scale: {
      low: '1 = AI slop tells. Over-stylized, plastic, uncanny.',
      high: '10 = reads as real filmed footage.',
    },
    weightNote: 'Weighted 2x',
    action: {
      label: 'Make it feel real',
      prompt:
        'Make the footage feel like real filmed video. Cut the over-stylized, glossy, or uncanny shots in favor of authentic-looking motion.',
    },
  },
  pacing: {
    id: 'video.pacing',
    label: 'Pacing',
    oneLiner: 'Do the beats build and vary, with none overstaying its welcome?',
    why: 'Pacing is the difference between watched and skipped. Static or saggy stretches lose people, and machine-gun cuts exhaust them. Beats should build and breathe.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    action: {
      label: 'Fix the pacing',
      prompt:
        'Re-cut the pacing so beats build and vary: trim anything that drags and even out cuts that feel too fast.',
    },
  },
  specificity: {
    id: 'video.specificity',
    label: 'Specificity',
    oneLiner: 'Swap test on the script and on-screen text: could only your business post it?',
    why: 'A generic script is a generic video. The same swap test applies: if a competitor could run this clip unchanged, it is not specific enough. Name real things from your business.',
    scale: DECK_SCALE,
    weightNote: 'Weighted 2x',
    tags: ['swap test'],
    action: {
      label: 'Make it specific',
      prompt:
        'Rewrite the script and on-screen text so only my business could post this clip: name real products, numbers, and moments.',
    },
  },
  captionLegible: {
    id: 'video.captionLegible',
    label: 'Captions',
    oneLiner:
      'Are captions and on-screen text readable, in the safe zone, and not covering the subject?',
    why: 'Most reels are watched on mute, so captions carry the message. Text that is too small, out of the safe zone, or laid over the subject loses the point for silent viewers.',
    scale: DECK_SCALE,
    action: {
      label: 'Fix the captions',
      prompt:
        'Make the captions and on-screen text fully legible: larger, inside the safe zone, and clear of the subject.',
    },
  },
  brandLook: {
    id: 'video.brandLook',
    label: 'Brand look',
    oneLiner: 'One cohesive look, with accent and type on-brand and no generic white template.',
    why: 'A reel that could belong to anyone builds no brand. A consistent look with your colors and type makes the clip recognizably yours even in a fast feed.',
    scale: DECK_SCALE,
    action: {
      label: 'Make it on-brand',
      prompt:
        'Give the reel a cohesive on-brand look: my accent colors and type, not a generic white template.',
    },
  },
  arc: {
    id: 'video.arc',
    label: 'Arc',
    oneLiner: 'Does hook to value to turn to CTA build as one job, with no repeated beats?',
    why: 'A reel needs a shape. Hook, then value, then a turn, then the ask. When beats repeat or wander, the clip feels long even when short. One job, start to finish.',
    scale: DECK_SCALE,
    action: {
      label: 'Tighten the arc',
      prompt:
        'Restructure the reel into a clear arc: hook, value, turn, CTA, doing one job with no repeated beats.',
    },
  },
  cta: {
    id: 'video.cta',
    label: 'CTA',
    oneLiner:
      'A real business action with a mechanism on the last beat, not bait or an abrupt cut.',
    why: 'The last beat is where attention becomes action. A genuine ask with a way to act (book, visit, DM, try) beats an abrupt ending or a generic "follow for more".',
    scale: DECK_SCALE,
    action: {
      label: 'Land the CTA',
      prompt:
        'End on a real call to action with a clear mechanism (book, visit, DM, try), not bait or an abrupt cut.',
    },
  },
  coherence: {
    id: 'video.coherence',
    label: 'Coherence',
    oneLiner:
      'Do the voiceover, on-screen text, and scene each carry different load, not triple-echo the same words?',
    why: 'When the VO, the captions, and the visual all say the same thing, the reel wastes two of its three channels. Coherence rewards each layer doing distinct work toward one message.',
    scale: DECK_SCALE,
    action: {
      label: 'Split the channels',
      prompt:
        'Make the voiceover, on-screen text, and visuals each carry different weight instead of repeating the same words.',
    },
  },
  finish: {
    id: 'video.finish',
    label: 'Finish',
    oneLiner: 'Overall postable polish across the frames: would a top brand publish this as is?',
    why: 'Finish is the whole-clip read. Rough transitions, off frames, or an unfinished feel pull it below the bar. The test is whether a brand you admire would post it unchanged.',
    scale: DECK_SCALE,
    action: {
      label: 'Raise the finish',
      prompt:
        'Raise the overall finish so a top brand would post this as is: clean transitions, strong frames, nothing unfinished.',
    },
  },
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

const DIMENSION_SETS: Record<ScoreRubric, Record<string, Explainer>> = {
  static: STATIC,
  carousel: CAROUSEL,
  video: VIDEO,
}

/**
 * The actionable, plain-language phrase for a verdict. Used as the collapsed card's
 * headline next to the score, so a newcomer reads a recommendation, not engine jargon.
 */
const VERDICT_HEADLINE: Record<string, string> = {
  SHIP: 'Ready to publish',
  SHIP_WITH_ONE_FIX: 'Ready, with one fix',
  REVISE: 'Needs a revision',
  REJECT: 'Start over',
  NO_SHIP_GATE_FAIL: 'Blocked by a check',
  GREAT: 'Great, ready to publish',
  STRONG: 'Strong, ready to publish',
  COMPETENT: 'Solid, could be sharper',
  TEMPLATE: 'Reads as a template',
}

export function verdictHeadline(verdict: string): string {
  return VERDICT_HEADLINE[verdict] ?? verdict
}

/** Plain tier word for the score band (strong/solid/weak), for the collapsed sub-line. */
export function tierLabel(tier: 'strong' | 'solid' | 'weak'): string {
  return tier === 'strong' ? 'Strong' : tier === 'solid' ? 'Solid' : 'Needs work'
}

/** Plain-language label for the art-direction tier (e.g. TEMPLATE -> "Looks templated"). */
export function designTierLabel(tier: string): string {
  return DESIGN[tier.toUpperCase()]?.label.replace(/ look$/, '') ?? tier
}

/**
 * Resolve any registered explainer by its fully-qualified id (the value passed to
 * openExplainer). `rubric` disambiguates the shared verdict tokens
 * (GREAT/STRONG/COMPETENT/TEMPLATE) so a reel gets reel copy, not carousel copy.
 */
export function getExplainerById(id: string, rubric?: ScoreRubric): Explainer | undefined {
  const [ns, key] = id.split('.')
  if (!key) return undefined
  switch (ns) {
    case 'concept':
      return CONCEPTS[key as 'quality' | 'weights' | 'swapTest']
    case 'gate':
      return GATES[key as keyof RubricGates]
    case 'verdict':
      return (rubric === 'video' && VIDEO_VERDICTS[key]) || VERDICTS[key]
    case 'design':
      return DESIGN[key]
    case 'static':
    case 'carousel':
    case 'video':
      return DIMENSION_SETS[ns][key]
    default:
      return undefined
  }
}
